/* eslint-disable indent */
import React, { useContext, useState, useEffect } from 'react';
import { Snackbar } from '@material-ui/core';
import { GlobalContext } from '@mummssoftware/global-context/GlobalContext';
import {
  ProgressNotes as PNotes,
  error,
  warning,
} from '@mummssoftware/utils/constants';
import { Watermark, WidgetWrapper } from '@mummssoftware/common-ui';
import { PNCounterContext } from '@mummssoftware/global-context/PNCounter';
import { REFRESH_PN } from '@mummssoftware/global-context/PNCounter/constants';
import MuiAlert, { AlertProps } from '@material-ui/lab/Alert';
import Notes from './Notes';
import CarePlanErrorDialog from './CarePlanErrorDialog';
import { Context } from '../../context';
import { ProgressNotesContext } from '../Context/ProgressNotesContext';
import { noteOrdersExpanded } from '../Context/constants';

type ProgressNotesProps = {
  smallWidget?: boolean;
};

export const ProgressNotes = React.memo(
  ({ smallWidget }: ProgressNotesProps) => {
    const {
      watermark,
      alertMessage,
      handleAlertMsg,
      appError,
      isFilterOn,
      entityType,
      entityKey,
      getFilteredNotes,
      admitErrorCount,
    } = useContext(Context);

    const { widgetsFilter } = useContext(GlobalContext);
    const { cancelWorkflow, apiError } = useContext(ProgressNotesContext);
    const pnContext = useContext(PNCounterContext);
    const pnFilter = widgetsFilter && widgetsFilter[PNotes];

    // Error with API calls from main context and PN context
    // todo strange strategy - need to harmonize
    const errorCallMsg: any = alertMessage || appError || apiError;

    const [alertOpen, setAlertOpen] = useState<boolean>(!!errorCallMsg);

    useEffect(() => {
      setAlertOpen(!!errorCallMsg);
    }, [errorCallMsg]);

    // when saving a note need to update the icon tagging of PN
    // Scenario : the notes are filtered to this entity
    // todo not the best solution, should happen in the function itself
    // @note PN
    useEffect(() => {
      if (isFilterOn && isFilterOn > 0) {
        // dispatch if present if from dashboard
        pnContext.dispatch &&
          pnContext.dispatch({
            type: REFRESH_PN,
            payload: {
              refreshEntity: entityType,
              refreshEntityKey: entityKey,
            },
          });
      }
    }, [isFilterOn]);

    // @note PN
    useEffect(() => {
      if (pnFilter && entityKey && pnFilter.entityKey !== entityKey) {
        getFilteredNotes(pnFilter);
      }
    }, [pnFilter && JSON.stringify(pnFilter)]);

    useEffect(() => {
      if (errorCallMsg) {
        if (admitErrorCount > 0) {
          // do not stop the error alert to show
          if (admitErrorCount < 5) {
            // do not stop the error alert to show
          } else {
            // went over max failures
            setTimeout(() => {
              setAlertOpen(false);
              handleAlertMsg();
            }, 8000);
          }
        } else {
          setTimeout(() => {
            setAlertOpen(false);
            handleAlertMsg();
          }, 7000);
        }
      }
    }, [errorCallMsg]);

    // If error in API call then stop the workflow.
    useEffect(() => {
      if (alertMessage && alertMessage.severity === error) {
        cancelWorkflow();
      }
    }, [alertMessage]);

    const Alert = (props: AlertProps) => (
      <MuiAlert elevation={6} variant="filled" {...props} />
    );
    const handleClose = () => {
      setAlertOpen(false);
      handleAlertMsg();
    };

    const noteWrapperId = smallWidget
      ? 'note-orders-wrapper'
      : noteOrdersExpanded;

    return (
      <WidgetWrapper id={noteWrapperId} smallWidget={smallWidget}>
        {watermark && watermark.show ? (
          <Watermark readOnly={watermark.readOnly} smallWidget={smallWidget} />
        ) : null}
        <Notes />
        <Snackbar
          open={alertOpen}
          autoHideDuration={
            admitErrorCount || errorCallMsg?.severity === warning
              ? undefined
              : 8000
          }
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Alert onClose={handleClose} severity={errorCallMsg?.severity}>
            {errorCallMsg?.message}
          </Alert>
        </Snackbar>
        <CarePlanErrorDialog />
      </WidgetWrapper>
    );
  },
);

export default ProgressNotes;
